// Terms of verbs: persons, tenses, moods, voice, non-finite forms, classes, prefixes and suffixes
// (Kielioppi § 5, 05-verbs.mdx; lessons 14–45). HV facts as 05-verbs.mdx states them (wiki:
// High_Valyrian_Verbs and its mood/tense pages, the reference grammar, Tips & Notes).
import { defineTerms } from './define';

export const terms = defineTerms({
  verbi: {
    fi: {
      name: 'verbi',
      short: '**Verbi** eli teonsana kertoo, mitä tehdään tai tapahtuu, kuten *nähdä* tai *olla*.',
      compare: 'Sama sanaluokka kuin suomessa, mutta valyriassa verbi on tavallisesti lauseen viimeisenä.',
    },
    en: {
      name: 'verb',
      short: 'A **verb** tells what is done or happens, such as *see* or *be*.',
      compare: 'The same word class as in Finnish, but in High Valyrian the verb usually comes last in the clause.',
    },
    related: ['persoonapaate', 'aikamuoto', 'sanajarjestys'],
    firstIn: 'present',
  },
  persoonapaate: {
    fi: {
      name: 'persoonapääte',
      short: '**Persoonapääte** on verbin pääte, joka kertoo tekijän persoonan ja luvun: *näe|n*, *näe|t*.',
      compare: 'Kuten suomessa, joten pronominin voi jättää pois; valyriassa myös 3. persoonassa.',
      long: 'Valyriassa persoonapäätteet riippuvat siitä, päättyykö verbin vartalo vokaaliin vai konsonanttiin. Kieliopin luetteloissa kuusi päätettä ovat aina järjestyksessä minä, sinä, hän, me, te, he.',
    },
    en: {
      name: 'person ending',
      short: 'A **person ending** is the verb ending that shows the doer’s person and number: Finnish *näe|n*, *näe|t*.',
      compare: 'As in Finnish, so the pronoun can be dropped; in High Valyrian in the 3rd person too.',
      long: 'In High Valyrian the endings depend on whether the verb’s stem ends in a vowel or a consonant. In the grammar’s lists the six endings always come in the order I, you, he/she, we, you, they.',
    },
    related: ['persoona', 'vokaalivartalo', 'konsonanttivartalo'],
    firstIn: 'present',
  },
  vokaalivartalo: {
    fi: {
      name: 'vokaalivartalo',
      short: '**Vokaalivartaloisen** verbin vartalo päättyy vokaaliin; sen infinitiivi päättyy [[-gon]].',
      compare: 'Suomen verbityypit ovat sama ajatus: vartalon loppu ratkaisee päätteet.',
      long: 'Tyyppiä ei aina näe infinitiivistä: a-vartaloinen verbi näyttää konsonanttivartaloiselta. Monikon 3. persoona paljastaa eron, joten opettele uuden verbin mukana sen ’he’-muoto.',
    },
    en: {
      name: 'vowel stem',
      short: 'A **vowel-stem** verb has a stem ending in a vowel; its infinitive ends in [[-gon]].',
      compare: 'Finnish verb types are the same idea: the end of the stem decides the endings.',
      long: 'The infinitive does not always show the type: an a-stem verb looks like a consonant-stem one. The 3rd person plural tells them apart, so learn a new verb’s ‘they’ form with it.',
    },
    related: ['konsonanttivartalo', 'vartalo', 'persoonapaate', 'infinitiivi'],
    firstIn: 'present',
  },
  konsonanttivartalo: {
    fi: {
      name: 'konsonanttivartalo',
      short: '**Konsonanttivartaloisen** verbin vartalo päättyy konsonanttiin; sen infinitiivi päättyy [[-agon]].',
      compare: 'Suomessakin vartalon loppu ratkaisee päätteet (*tul|la*, *sano|a*).',
      long: 'Konsonantin laatu ratkaisee kaksi hankalaa asiaa: yksikön 3. persoonan päätteen ja perfektivartalon.',
    },
    en: {
      name: 'consonant stem',
      short: 'A **consonant-stem** verb has a stem ending in a consonant; its infinitive ends in [[-agon]].',
      compare: 'In Finnish too the end of the stem decides the endings (*tul|la*, *sano|a*).',
      long: 'Which consonant it is decides two tricky things: the 3rd person singular ending and the perfect stem.',
    },
    related: ['vokaalivartalo', 'vartalo', 'perfektivartalo'],
    firstIn: 'present',
  },
  juuri: {
    fi: {
      name: 'juuri',
      short: '**Juuri** on sanan ydin, jonka ympärille etuliitteet, johtimet ja päätteet rakentuvat.',
      compare: 'Kuten *kirja* sanoissa *kirjasto* ja *kirjoittaa*; vartalo on juuri ja se, mitä siihen on jo liitetty.',
    },
    en: {
      name: 'root',
      short: 'A **root** is the core of a word, around which prefixes, suffixes and endings are built.',
      compare: 'Like Finnish *kirja* in *kirjasto* and *kirjoittaa*; a stem is the root plus whatever is already attached to it.',
    },
    related: ['vartalo', 'johdin', 'etuliite'],
    firstIn: 'perfect',
  },
  aikamuoto: {
    fi: {
      name: 'aikamuoto',
      short: '**Aikamuoto** on verbin muoto, joka kertoo, milloin teko tapahtuu ja miten sitä katsotaan.',
      compare: 'Suomessa aikamuotoja on neljä (preesens, imperfekti, perfekti, pluskvamperfekti), valyriassa seitsemän, ja niiden työnjako on toinen.',
      long: 'Valyrian aikamuodot ovat preesens, aoristi, futuuri, imperfekti, perfekti, pluskvamperfekti ja mennyt toistuva. Huomaa, että valyrian imperfekti ei ole sama kuin suomen koulukieliopin imperfekti.',
    },
    en: {
      name: 'tense',
      short: 'A **tense** is a verb form that tells when an action happens and how it is seen.',
      compare: 'Finnish has four tenses (present, imperfect, perfect, pluperfect), High Valyrian seven, and they divide the work differently.',
      long: 'The High Valyrian tenses are the present, aorist, future, imperfect, perfect, pluperfect and past habitual. Note that the Valyrian imperfect is not the Finnish school-grammar imperfekti.',
    },
    related: ['preesens', 'aoristi', 'futuuri', 'imperfekti', 'perfekti', 'pluskvamperfekti', 'mennyt-toistuva', 'aspekti'],
    firstIn: 'present',
  },
  aspekti: {
    fi: {
      name: 'aspekti',
      short: '**Aspekti** kertoo, miten tekoa katsotaan: onko se käynnissä, valmis vai tapana toistuva, eikä niinkään sitä, milloin se tapahtuu.',
      compare: 'Suomi ilmaisee saman eron usein objektin sijalla: *luin kirjaa* (kesken) ja *luin kirjan* (loppuun); valyriassa ero näkyy verbissä.',
    },
    en: {
      name: 'aspect',
      short: '**Aspect** tells how an action is seen: in progress, finished or habitual, rather than when it happens.',
      compare:
        'Finnish often shows the same difference with the object’s case: *luin kirjaa* (unfinished) and *luin kirjan* (to the end); in High Valyrian the verb shows it.',
    },
    related: ['aikamuoto', 'perfekti', 'imperfekti'],
    firstIn: 'perfect',
  },
  preesens: {
    fi: {
      name: 'preesens',
      short: '**Preesens** kertoo, mitä tapahtuu juuri nyt; sillä puhutaan myös lähitulevaisuudesta.',
      compare: 'Suomen preesens kattaa myös aina pätevät asiat (*koirat haukkuvat*), mutta valyriassa niihin on oma aikamuotonsa, aoristi.',
    },
    en: {
      name: 'present',
      short: 'The **present** tells what is happening right now; it is also used for the near future.',
      compare: 'The Finnish present also covers what is always true (*koirat haukkuvat*), but High Valyrian has its own tense for that, the aorist.',
    },
    related: ['aoristi', 'futuuri', 'aikamuoto'],
    firstIn: 'present',
  },
  aoristi: {
    fi: {
      name: 'aoristi',
      short: '**Aoristi** on yleisten totuuksien, luonteen, tapojen ja taitojen aikamuoto: se kertoo, mikä pitää aina paikkansa.',
      compare: 'Suomessa ei ole omaa muotoa: preesens hoitaa sekä uutisen että luonnonlain (*koirat haukkuvat*).',
      long: 'Lause [[Valar morghūlis]] on aoristi: ’kaikki ihmiset kuolevat’ on luonnonlaki eikä uutinen. Kysy itseltäsi: tapahtuuko tämä nyt, vai pitääkö se aina paikkansa?',
    },
    en: {
      name: 'aorist',
      short: 'The **aorist** is the tense of general truths, character, habits and skills: it tells what is always so.',
      compare: 'Finnish has no form of its own: the present does both the news and the law of nature (*koirat haukkuvat*).',
      long: '[[Valar morghūlis]] is an aorist: ‘all men die’ is a law of nature, not news. Ask yourself: is this happening now, or is it always so?',
    },
    related: ['preesens', 'mennyt-toistuva', 'aikamuoto'],
    firstIn: 'aorist',
  },
  futuuri: {
    fi: {
      name: 'futuuri',
      short: '**Futuuri** on tulevan ajan aikamuoto kauempana olevista ennusteista ja lupauksista.',
      compare: 'Suomessa ei ole futuurin taivutusmuotoa: sanomme *puhun huomenna* tai *aion puhua*.',
      long: 'Futuurin tunnus on *l*. Kun asia tapahtuu aivan pian, käytetään preesensiä.',
    },
    en: {
      name: 'future',
      short: 'The **future** is the tense of predictions further ahead and of promises.',
      compare: 'Finnish has no future form: it says *puhun huomenna* or *aion puhua*.',
      long: 'The future is marked by *l*. For something very soon the present is used.',
    },
    related: ['preesens', 'aikamuoto'],
    firstIn: 'future',
  },
  imperfekti: {
    fi: {
      name: 'imperfekti',
      short: '**Imperfekti** on valyriassa aikamuoto, joka kuvaa menneisyydessä käynnissä ollutta tekoa: ’olin syöttämässä’.',
      compare: 'Varo nimeä: se ei ole suomen koulukieliopin imperfekti (*luin*), vaan vastaa rakennetta *olin lukemassa*.',
      long: 'Suomen *luin* on valyriassa yleensä perfekti. Imperfekti antaa kertomukselle taustan, jonka keskellä jotain tapahtui, ja sopii tekosyihin (’olin juuri…’).',
    },
    en: {
      name: 'imperfect',
      short: 'The **imperfect** is the High Valyrian tense for an action that was going on in the past: ‘I was feeding’.',
      compare: 'Beware the name: it is not the Finnish school-grammar imperfekti (*luin*) but matches *olin lukemassa*.',
      long: 'Finnish *luin* is usually a perfect in High Valyrian. The imperfect gives a story its background, against which something happened, and suits excuses (‘I was just…’).',
    },
    related: ['perfekti', 'aspekti', 'aikamuoto'],
    firstIn: 'imperfect',
  },
  perfekti: {
    fi: {
      name: 'perfekti',
      short: '**Perfekti** kertoo, että teko on valmis.',
      compare: 'Suomeksi sen voi kääntää kummalla tahansa menneen ajan muodolla: ’näin’ tai ’olen nähnyt’.',
      long: 'Suomen tavallinen mennyt aika (koulukieliopin imperfekti, *söin*) vastaa valyriassa yleensä perfektiä. Perfekti rakentuu perfektivartalolle, useimmiten juuri + *t*.',
    },
    en: {
      name: 'perfect',
      short: 'The **perfect** tells that an action is finished.',
      compare: 'In Finnish it can be either past form: ‘näin’ or ‘olen nähnyt’.',
      long: 'The ordinary Finnish past (the school-grammar imperfekti, *söin*) is usually a perfect in High Valyrian. The perfect is built on the perfect stem, most often the root + *t*.',
    },
    related: ['perfektivartalo', 'imperfekti', 'pluskvamperfekti', 'aspekti'],
    firstIn: 'perfect',
  },
  perfektivartalo: {
    fi: {
      name: 'perfektivartalo',
      short: '**Perfektivartalo** on verbin vartalo, jolle perfekti, pluskvamperfekti ja mennyt toistuva rakentuvat, useimmiten juuri + *t*.',
      compare: 'Perfektin *t* sulautuu edeltävään konsonanttiin samaan tapaan kuin suomen *-nut*: *tullut*, *purrut*, *pessyt*.',
      long: 'Perfektivartalo on usein arvaamaton, ja wiki neuvoo opettelemaan sen verbi kerrallaan.',
    },
    en: {
      name: 'perfect stem',
      short: 'The **perfect stem** is the stem the perfect, pluperfect and past habitual are built on, most often the root + *t*.',
      compare: 'The perfect *t* merges with the consonant before it much as Finnish *-nut* does: *tullut*, *purrut*, *pessyt*.',
      long: 'The perfect stem is often unpredictable, and the wiki advises learning it verb by verb.',
    },
    related: ['perfekti', 'juuri', 'konsonanttivartalo'],
    firstIn: 'perfect',
  },
  pluskvamperfekti: {
    fi: {
      name: 'pluskvamperfekti',
      short: '**Pluskvamperfekti** kertoo teosta, joka oli jo valmis ennen toista menneisyyden hetkeä: ’olin syönyt’.',
      compare: 'Kuten suomen *olin syönyt*.',
      long: 'Valyriassa todisteita on vähän: wikin mukaan vain kaksi muotoa tunnetaan, ja muut ovat päätelmiä.',
    },
    en: {
      name: 'pluperfect',
      short: 'The **pluperfect** tells of an action already finished before another moment in the past: ‘I had eaten’.',
      compare: 'Like Finnish *olin syönyt*.',
      long: 'There is little evidence: by the wiki only two forms are known, and the rest are inferred.',
    },
    related: ['perfekti', 'mennyt-toistuva'],
    firstIn: 'pluperfect',
  },
  'mennyt-toistuva': {
    fi: {
      name: 'mennyt toistuva',
      short: '**Mennyt toistuva** kertoo, mitä ennen oli tapana tehdä: ’minulla oli tapana’, englannin *used to*.',
      compare: 'Suomessa tätä aikamuotoa ei ole: tapa ilmaistaan sanoilla (*ennen*, *oli tapana*) tai johtimella (*lueskelin*).',
      long: 'Se on perfektivartalo ja aoristin päätteet, ikään kuin menneisyyden aoristi. Nyrkkisääntö: jos sanoisit suomeksi ”ennen” tai ”oli tapana”, valitse mennyt toistuva.',
    },
    en: {
      name: 'past habitual',
      short: 'The **past habitual** tells what someone used to do: ‘I used to’.',
      compare: 'Finnish has no such tense: a habit is said with words (*ennen*, *oli tapana*) or a suffix (*lueskelin*).',
      long: 'It is the perfect stem with the aorist endings, a past aorist of sorts. Rule of thumb: if you would say ”ennen” or ”oli tapana” in Finnish, use the past habitual.',
    },
    related: ['aoristi', 'perfektivartalo', 'pluskvamperfekti'],
    firstIn: 'pluperfect',
  },
  tapaluokka: {
    fi: {
      name: 'tapaluokka',
      short: '**Tapaluokka** eli modus kertoo, esitetäänkö asia totena, kuviteltuna vai käskynä.',
      compare:
        'Suomen tapaluokkia ovat indikatiivi (*tulen*), konditionaali (*tulisin*), imperatiivi (*tule!*) ja potentiaali (*tullen*); valyriassa konditionaalin paikalla on usein subjunktiivi.',
      long: 'Valyrian tapaluokat ovat indikatiivi, subjunktiivi ja imperatiivi.',
    },
    en: {
      name: 'mood',
      short: '**Mood** tells whether something is presented as true, imagined or commanded.',
      compare:
        'The Finnish moods are the indicative (*tulen*), conditional (*tulisin*), imperative (*tule!*) and potential (*tullen*); where Finnish uses the conditional, High Valyrian often has the subjunctive.',
      long: 'The High Valyrian moods are the indicative, subjunctive and imperative.',
    },
    related: ['indikatiivi', 'subjunktiivi', 'imperatiivi', 'konditionaali'],
    firstIn: 'subjunctive',
  },
  indikatiivi: {
    fi: {
      name: 'indikatiivi',
      short: '**Indikatiivi** on tavallisen toteamuksen tapaluokka: asia esitetään totena.',
      compare: 'Kuten suomen *tulen*, *tulin*; valyriassa myönteinen väite on indikatiivissa, mutta kieltosana [[daor]] vaihtaa verbin subjunktiiviin.',
    },
    en: {
      name: 'indicative',
      short: 'The **indicative** is the mood of plain statements: something is presented as true.',
      compare: 'Like Finnish *tulen*, *tulin*; a positive statement is indicative, but the negator [[daor]] switches the verb to the subjunctive.',
    },
    related: ['tapaluokka', 'subjunktiivi'],
    firstIn: 'subjunctive',
  },
  subjunktiivi: {
    fi: {
      name: 'subjunktiivi',
      short: '**Subjunktiivi** on kuviteltujen, epävarmojen ja epätodellisten asioiden tapaluokka; valyriassa sitä käytetään myös kiellossa.',
      compare:
        'Suomessa ei ole subjunktiivia; lähimpänä on konditionaali (*tulisin*), mutta valyrian kieltolauseessa verbi on subjunktiivissa ja kieltosana ei taivu.',
      long: 'Subjunktiivin laukaisevat esimerkiksi kielto [[daor]], toive, kuviteltu ehto ja tarkoitus. Älä sekoita sitä konjunktiiviseen pidennykseen, joka on ’ja’-sanan korvaava pidennys.',
    },
    en: {
      name: 'subjunctive',
      short: 'The **subjunctive** is the mood of imagined, uncertain and unreal things; High Valyrian also uses it in negation.',
      compare:
        'Finnish has no subjunctive; the nearest is the conditional (*tulisin*), but in a High Valyrian negative clause the verb is subjunctive and the negator does not inflect.',
      long: 'The subjunctive is triggered by, for example, the negator [[daor]], wishes, imagined conditions and purpose. Do not mix it up with the conjunctive lengthening, the lengthening that stands for ‘and’.',
    },
    related: ['tapaluokka', 'indikatiivi', 'konditionaali', 'konjunktiivinen-pidennys'],
    firstIn: 'subjunctive',
  },
  imperatiivi: {
    fi: {
      name: 'imperatiivi',
      short: '**Imperatiivi** on käskymuoto: *syö!*, *syökää!*',
      compare:
        'Suomen imperatiivissa on myös 3. persoona (*syököön*), valyrian imperatiivissa vain 2. persoona, yksikkö ja monikko, eikä sitä voi kieltää: kielto on infinitiivi ja [[daor]].',
      long: 'Imperatiivissa paino on viimeisellä tavulla. Käsky jollekulle muulle on vokatiivi ja infinitiivi, kuten suomen *pysähtyköön*.',
    },
    en: {
      name: 'imperative',
      short: 'The **imperative** is the command form: *eat!*',
      compare:
        'Finnish also has a 3rd person imperative (*syököön*); High Valyrian has only the 2nd person, singular and plural, and it cannot be negated: a negative command is the infinitive and [[daor]].',
      long: 'Imperatives are stressed on the last syllable. A command to someone else is a vocative with an infinitive, like Finnish *pysähtyköön*.',
    },
    related: ['tapaluokka', 'infinitiivi', 'vokatiivi', 'paino'],
    firstIn: 'imperative',
  },
  aktiivi: {
    fi: {
      name: 'aktiivi',
      short: '**Aktiivi** on verbin tavallinen muoto, jossa subjekti tekee teon: *mies näkee naisen*.',
      compare: 'Kuten suomessa.',
    },
    en: {
      name: 'active',
      short: 'The **active** is the ordinary verb form, in which the subject does the action: *the man sees the woman*.',
      compare: 'As in Finnish.',
    },
    related: ['passiivi', 'paaluokka'],
    firstIn: 'passive',
  },
  passiivi: {
    fi: {
      name: 'passiivi',
      short: '**Passiivi** on verbin muoto, jossa teon kohteesta tulee lauseen subjekti: ’nainen tulee nähdyksi’.',
      compare:
        'Suomen passiivi on persoonaton (*minut nähtiin*); valyrian passiivi on persoonallinen: kohde on nominatiivissa, verbi taipuu sen mukaan, ja tekijän voi mainita.',
      long: 'Passiivin tunnus on *-ks*. Tekijä on tavallisesti genetiivissä, ja sen perässä on postpositio, joka on sanan ’käsi’ instrumentaali.',
    },
    en: {
      name: 'passive',
      short: 'The **passive** is a verb form in which the object of the action becomes the subject: ‘the woman is seen’.',
      compare:
        'The Finnish passive is impersonal (*minut nähtiin*); the High Valyrian one is personal: the object is nominative, the verb agrees with it, and the doer can be named.',
      long: 'The passive is marked by *-ks*. The doer is usually in the genitive, followed by a postposition that is the instrumental of ‘hand’.',
    },
    related: ['aktiivi', 'paaluokka', 'valinepassiivi', 'persoonaton'],
    firstIn: 'passive',
  },
  paaluokka: {
    fi: {
      name: 'pääluokka',
      short: '**Pääluokka** kertoo, onko verbi aktiivissa (subjekti tekee) vai passiivissa (subjektille tehdään).',
      compare: 'Suomessakin on aktiivi ja passiivi, mutta suomen passiivi on persoonaton.',
    },
    en: {
      name: 'voice',
      short: '**Voice** tells whether a verb is active (the subject acts) or passive (the subject is acted on).',
      compare: 'Finnish has an active and a passive too, but the Finnish passive is impersonal.',
    },
    related: ['aktiivi', 'passiivi'],
    firstIn: 'passive',
  },
  persoonaton: {
    fi: {
      name: 'persoonaton verbi',
      short: '**Persoonaton verbi** on yksikön 3. persoonassa ilman subjektia, kuten suomen *sataa*.',
      compare: 'Suomessa samoin: *sataa*, *täytyy*.',
    },
    en: {
      name: 'impersonal verb',
      short: 'An **impersonal verb** stands in the 3rd person singular with no subject, like Finnish *sataa* ‘it rains’.',
      compare: 'The same in Finnish: *sataa*, *täytyy*.',
    },
    related: ['passiivi', 'subjekti'],
    firstIn: 'passive',
  },
  infinitiivi: {
    fi: {
      name: 'infinitiivi',
      short: '**Infinitiivi** on verbin perusmuoto, jota ei ole taivutettu persoonassa: *nähdä*.',
      compare: 'Valyrian infinitiivi on myös sanakirjamuoto, kuten suomen 1. infinitiivi; sen pääte on [[-gon]] tai [[-agon]].',
      long: 'Infinitiivejä on kuusi: preesens, aoristi ja perfekti, kukin aktiivissa ja passiivissa; futuurin infinitiiviä ei ole. Infinitiivi tulee pääverbin edelle, ja kielletty käsky on infinitiivi ja [[daor]].',
    },
    en: {
      name: 'infinitive',
      short: 'The **infinitive** is the basic verb form, not inflected for person: *to see*.',
      compare: 'The High Valyrian infinitive is also the dictionary form, like the Finnish 1st infinitive; it ends in [[-gon]] or [[-agon]].',
      long: 'There are six: present, aorist and perfect, each active and passive; there is no future infinitive. It comes before the main verb, and a negative command is the infinitive with [[daor]].',
    },
    related: ['verbi', 'imperatiivi', 'vokaalivartalo', 'konsonanttivartalo'],
    firstIn: 'infinitives',
  },
  partisiippi: {
    fi: {
      name: 'partisiippi',
      short: '**Partisiippi** on verbin muoto, joka toimii adjektiivina: *syövä*, *syönyt*, *syötävä*.',
      compare: 'Suomen partisiipit ovat sama ajatus, vain eri tavalla jaoteltu; valyrian partisiippi mukautuu suvussa, luvussa ja sijassa kuten adjektiivi.',
      long: 'Partisiipin täydennys tulee sen eteen kuten suomessa: ’oikeudenkäyntiä odottava’. Partisiippia ei voi kieltää [[daor]]-sanalla.',
    },
    en: {
      name: 'participle',
      short: 'A **participle** is a verb form that works as an adjective: *eating*, *eaten*.',
      compare:
        'Finnish participles are the same idea, only divided differently; a High Valyrian participle agrees in gender, number and case like an adjective.',
      long: 'Its complement comes before it, as in Finnish: ‘awaiting trial’. A participle cannot be negated with [[daor]].',
    },
    related: ['adjektiivi', 'perifrastinen-passiivi', 'transitiivinen'],
    firstIn: 'participles',
  },
  'perifrastinen-passiivi': {
    fi: {
      name: 'perifrastinen passiivi',
      short: '**Perifrastinen passiivi** on kahdesta sanasta rakennettu passiivi: perfektin partisiippi ja olla-verbi.',
      compare: 'Suomen *on lopetettu* on rakenteeltaan samanlainen; valyriassa tämä tapa on harvinainen, ja tavallinen passiivi tehdään *-ks*-päätteellä.',
    },
    en: {
      name: 'periphrastic passive',
      short: 'The **periphrastic passive** is a passive built from two words: the perfect participle and ‘be’.',
      compare: 'Finnish *on lopetettu* is built the same way; in High Valyrian this is rare, and the ordinary passive takes *-ks*.',
    },
    related: ['partisiippi', 'passiivi'],
    firstIn: 'participles',
  },
  transitiivinen: {
    fi: {
      name: 'transitiivinen',
      short: '**Transitiivinen** verbi saa objektin (*kirjoittaa kirje*), **intransitiivinen** ei (*kuolla*).',
      compare: 'Jako on sama kuin suomessa.',
      long: 'Valyrian perfektin partisiippi on transitiiviverbeillä passiivinen (’kirjoitettu’) ja intransitiiviverbeillä aktiivinen (’kuollut’).',
    },
    en: {
      name: 'transitive',
      short: 'A **transitive** verb takes an object (*write a letter*), an **intransitive** one does not (*die*).',
      compare: 'The split is the same as in Finnish.',
      long: 'The perfect participle is passive for transitive verbs (‘written’) and active for intransitive ones (‘dead’).',
    },
    related: ['objekti', 'passiivi', 'partisiippi'],
    firstIn: 'participles',
  },
  valenssi: {
    fi: {
      name: 'valenssi',
      short: '**Valenssi** kertoo, mitä täydennyksiä verbi vaatii ja missä sijoissa: kuka tekee, mitä ja kenelle.',
      compare:
        'Suomessakin verbi määrää täydennyksen sijan (*pitää jostakin*, *odottaa jotakin*); valyriassa sijat ovat usein toiset, joten verbi ja sija opetellaan yhdessä.',
      long: 'Valenssin etuliitteet, kuten välinepassiivi ja applikatiivit, muuttavat sitä, kuka lauseessa on kuka.',
    },
    en: {
      name: 'valence',
      short: '**Valence** tells which complements a verb needs and in which cases: who does it, to what, for whom.',
      compare:
        'In Finnish too a verb decides its complement’s case (*pitää jostakin*, *odottaa jotakin*); in High Valyrian the cases are often different, so learn verb and case together.',
      long: 'The valence prefixes, such as the instrumental passive and the applicatives, change who is who in the clause.',
    },
    related: ['applikatiivi', 'valinepassiivi', 'objekti'],
    firstIn: 'valence',
  },
  applikatiivi: {
    fi: {
      name: 'applikatiivi',
      short:
        '**Applikatiivi** on verbin etuliite, joka tekee paikasta tai jostakin muusta lauseenosasta verbin objektin: ’itkeä jonkun puolesta’ → ’itkeä joku’.',
      compare: 'Suomessa ei ole vastinetta.',
      long: 'Valyriassa applikatiiveja on kaksi: lokatiivinen tekee paikasta objektin, obliikvinen esimerkiksi siitä, kenen puolesta jotain tehdään. Passiivissa applikatiivin objektista tulee subjekti: ’pöytä hypättiin päälle’.',
    },
    en: {
      name: 'applicative',
      short: 'An **applicative** is a verb prefix that turns a place or another adverbial into the verb’s object: ‘weep for someone’ → ‘weep someone’.',
      compare: 'Finnish has nothing like it.',
      long: 'High Valyrian has two: the locative applicative makes a place the object, the oblique applicative a beneficiary, for example. In the passive the applicative object becomes the subject: ‘the table was jumped on’.',
    },
    related: ['lokatiivinen-applikatiivi', 'obliikvinen-applikatiivi', 'valenssi', 'etuliite'],
    firstIn: 'prefixes',
  },
  'lokatiivinen-applikatiivi': {
    fi: {
      name: 'lokatiivinen applikatiivi',
      short: '**Lokatiivinen applikatiivi** on etuliite, joka tekee paikasta verbin objektin.',
      compare: 'Suomessa ei ole vastinetta; suomi käyttää paikallissijaa (*hyppäsi pöydälle*).',
      long: 'Sen objekti on heti verbin edellä. Petersonin mukaan se on genetiivissä, kun verbi alkaa konsonantilla, ja datiivissa, kun verbi alkaa vokaalilla.',
    },
    en: {
      name: 'locative applicative',
      short: 'The **locative applicative** is a prefix that makes a place the verb’s object.',
      compare: 'Finnish has nothing like it; it uses a local case (*hyppäsi pöydälle*).',
      long: 'Its object stands right before the verb. According to Peterson it is genitive when the verb starts with a consonant and dative when it starts with a vowel.',
    },
    related: ['applikatiivi', 'obliikvinen-applikatiivi'],
    firstIn: 'prefixes',
  },
  'obliikvinen-applikatiivi': {
    fi: {
      name: 'obliikvinen applikatiivi',
      short:
        '**Obliikvinen applikatiivi** on etuliite, joka tekee jostakin muusta lauseenosasta, esimerkiksi siitä, kenen puolesta jotain tehdään, verbin akkusatiiviobjektin: ’itkeä jonkun puolesta’.',
      compare: 'Suomessa ei ole vastinetta; suomi käyttää postpositiota (*jonkun puolesta*).',
    },
    en: {
      name: 'oblique applicative',
      short:
        'The **oblique applicative** is a prefix that turns another adverbial, a beneficiary for example, into the verb’s accusative object: ‘weep for someone’.',
      compare: 'Finnish has nothing like it; it uses a postposition (*jonkun puolesta*).',
    },
    related: ['applikatiivi', 'lokatiivinen-applikatiivi'],
    firstIn: 'prefixes',
  },
  valinepassiivi: {
    fi: {
      name: 'välinepassiivi',
      short:
        '**Välinepassiivi** on etuliitteellä tehty verbin muoto, jonka subjekti on eloton väline, joka ei voi toimia omasta tahdostaan: ’veitsi leikkaa leipää’.',
      compare: 'Suomessa ei ole vastinetta: suomi sanoo saman tavallisella verbillä.',
    },
    en: {
      name: 'instrumental passive',
      short:
        'The **instrumental passive** is a prefixed verb form whose subject is an inanimate tool that cannot act of its own will: ‘the knife cuts the bread’.',
      compare: 'Finnish has nothing like it: it says the same with an ordinary verb.',
    },
    related: ['valenssi', 'passiivi', 'etuliite'],
    firstIn: 'prefixes',
  },
  etuliite: {
    fi: {
      name: 'etuliite',
      short: '**Etuliite** on sanan alkuun liitettävä osa, joka muuttaa sen merkitystä.',
      compare: 'Suomessa etuliitteitä on vähän (*epä|reilu*); valyrian verbillä niitä voi olla useita, kiinteässä järjestyksessä.',
      long: 'Valyrian verbin etuliitteet tulevat juuresta ulospäin: lähinnä jatkuvuuden etuliite, sitten valenssin etuliite ja uloimpana adpositioetuliite. Etuliite ei muuta taivutusta.',
    },
    en: {
      name: 'prefix',
      short: 'A **prefix** is a part added to the start of a word that changes its meaning.',
      compare: 'Finnish has few prefixes (*epä|reilu*); a High Valyrian verb can have several, in a fixed order.',
      long: 'High Valyrian verb prefixes go outward from the root: continuity closest, then the valence prefix, then the adposition prefix outermost. A prefix does not change the inflection.',
    },
    related: ['applikatiivi', 'valinepassiivi', 'johdin', 'juuri'],
    firstIn: 'prefixes',
  },
  johdin: {
    fi: {
      name: 'johdin',
      short: '**Johdin** on pääte, jolla sanasta tehdään uusi sana: *kirja* → *kirja|sto*.',
      compare: 'Suomi on johtimien kieli (*kaatua* → *kaataa*), ja valyrian johtimet ovat yllättävän samanlaisia.',
      long: 'Johdos taipuu oman sanaluokkansa mukaan: substantiivijohdos saa oman suvun ja taivutusluokan. Monella johdoksella on idiomaattinen merkitys, joten johtimista on apua ennen kaikkea lukiessa.',
    },
    en: {
      name: 'derivational suffix',
      short: 'A **derivational suffix** makes a new word from a word: Finnish *kirja* → *kirja|sto*.',
      compare: 'Finnish is a language of such suffixes (*kaatua* → *kaataa*), and the High Valyrian ones are surprisingly similar.',
      long: 'A derived word inflects by its own word class: a derived noun gets its own gender and declension. Many derived words have an idiomatic meaning, so suffixes help most when reading.',
    },
    related: ['johdos', 'kausatiivi', 'eventatiivi', 'etuliite'],
    firstIn: 'suffixes',
  },
  johdos: {
    fi: {
      name: 'johdos',
      short: '**Johdos** on johtimella tai etuliitteellä toisesta sanasta tehty sana, kuten *kirjasto*.',
      compare:
        'Kuten suomessa; valyriassa johdos taipuu oman sanaluokkansa, suvunsa ja taivutusluokkansa mukaan, ei välttämättä kuten se sana, josta se on tehty.',
    },
    en: {
      name: 'derivative',
      short: 'A **derivative** is a word made from another word with a suffix or a prefix, like Finnish *kirjasto*.',
      compare:
        'As in Finnish; in High Valyrian a derivative inflects by its own word class, gender and declension, not necessarily like the word it was made from.',
    },
    related: ['johdin', 'yhdyssana'],
    firstIn: 'suffixes',
  },
  kausatiivi: {
    fi: {
      name: 'kausatiivi',
      short: '**Kausatiivi** on verbi, joka tarkoittaa ’saada joku tai jokin tekemään’: *kaatua* → *kaataa*.',
      compare: 'Kuten suomen *lähteä* → *lähettää*; valyriassa kausatiivin johdin on [[-ikagon]] tai [[-kagon]].',
    },
    en: {
      name: 'causative',
      short: 'A **causative** is a verb meaning ‘make someone or something do’: Finnish *kaatua* ‘fall’ → *kaataa* ‘fell’.',
      compare: 'Like Finnish *lähteä* → *lähettää*; the High Valyrian causative suffix is [[-ikagon]] or [[-kagon]].',
    },
    related: ['johdin'],
    firstIn: 'suffixes',
  },
  eventatiivi: {
    fi: {
      name: 'eventatiivi',
      short:
        '**Eventatiivi** on valyrian johdin [[-egon]], joka liitetään perfektivartaloon ja tekee verbin, joka kertoo tapahtuman tuloksesta: ’nähdä’ → ’havaita’.',
      compare: 'Suomessa ei ole vastinetta.',
    },
    en: {
      name: 'eventative',
      short:
        'The **eventative** is the High Valyrian suffix [[-egon]], added to the perfect stem, which makes a verb about the result of an event: ‘see’ → ‘perceive’.',
      compare: 'Finnish has nothing like it.',
    },
    related: ['johdin', 'perfektivartalo'],
    firstIn: 'suffixes',
  },
});
